import { useCallback, useRef } from "react";

import type { RuleDiffResponseDto } from "@/lib/rulediff-api";

/**
 * apps/web :: hooks/useRuleDiffCache
 *
 * Session-level cache for rule-diff responses keyed by
 * `${homeCountry}|${state}|${lang}`. The cache is a plain `Map` held in a
 * `useRef` so it survives re-renders but dies on full page refresh.
 *
 * Why session-only (no localStorage)?
 *   - Rule-diff content can change mid-day (handbook revision, ADR-0010
 *     moderator re-review). Persisting to localStorage would let a stale
 *     response live past that window. Session scope gives the user an
 *     instant back-and-forth between states within a single session while
 *     guaranteeing the next login fetches fresh.
 *
 * Behaviour:
 *   - `get(key)`: synchronous peek; returns undefined on miss.
 *   - `fetchOrGet(key, fetcher)`: hit → resolve immediately with the cached
 *     value; miss → await the fetcher, store on success, propagate on
 *     failure. Failures are NOT stored.
 *
 * The hook is compatible with an ADR-0010 forward-compat wire shape — the
 * stored value is typed as `RuleDiffResponseDto`, but callers may widen to
 * a superset (extra `status` field) without the cache caring.
 */

export interface UseRuleDiffCacheApi {
  /** Synchronous peek. Returns undefined on miss. */
  get(key: string): RuleDiffResponseDto | undefined;
  /** Hit: cached value. Miss: awaits fetcher, stores on success. */
  fetchOrGet(
    key: string,
    fetcher: () => Promise<RuleDiffResponseDto>,
  ): Promise<RuleDiffResponseDto>;
}

export function useRuleDiffCache(): UseRuleDiffCacheApi {
  // Map is held in a ref so it's stable across re-renders. The Map itself is
  // mutated in place — React never needs to re-render when the cache mutates
  // because the cache is not a render input; it's an effectful side-store.
  const cacheRef = useRef<Map<string, RuleDiffResponseDto>>(new Map());
  // Track in-flight promises so concurrent `fetchOrGet` calls (e.g. React
  // StrictMode's double-invoked effect, or a re-render that happens mid-
  // fetch) de-dupe onto a single network request. Success: the entry is
  // written to cacheRef and the in-flight entry is dropped. Failure: the
  // in-flight entry is also dropped so the next call retries cleanly.
  const inflightRef = useRef<Map<string, Promise<RuleDiffResponseDto>>>(
    new Map(),
  );

  const get = useCallback((key: string): RuleDiffResponseDto | undefined => {
    return cacheRef.current.get(key);
  }, []);

  const fetchOrGet = useCallback(
    async (
      key: string,
      fetcher: () => Promise<RuleDiffResponseDto>,
    ): Promise<RuleDiffResponseDto> => {
      const hit = cacheRef.current.get(key);
      if (hit !== undefined) return hit;
      const inflight = inflightRef.current.get(key);
      if (inflight !== undefined) return inflight;
      // Intentionally NOT caught — the caller surfaces errors. A failed fetch
      // must not poison the cache; the next call retries cleanly.
      const promise = (async () => {
        try {
          const resolved = await fetcher();
          cacheRef.current.set(key, resolved);
          return resolved;
        } finally {
          inflightRef.current.delete(key);
        }
      })();
      inflightRef.current.set(key, promise);
      return promise;
    },
    [],
  );

  return { get, fetchOrGet };
}

/**
 * Helper the dashboard uses to build the Map key consistently across the
 * hook's callers. Kept free-standing so the cache-key convention isn't
 * duplicated at each call site.
 */
export function ruleDiffCacheKey(
  homeCountry: string,
  state: string,
  lang: string,
): string {
  return `${homeCountry}|${state}|${lang}`;
}
